<template>
  <!-- "white": el CSS global deja transparentes los v-card y los fondos del tab se transparentan (V1 usa lo mismo en su stepper). -->
  <div class="product-import-v2 white elevation-2 rounded pa-4 ma-3">
    <v-card flat class="pa-4 mb-4">
      <div class="d-flex flex-wrap justify-space-between align-center">
        <div>
          <h2 class="mb-1">Importación de productos V2</h2>
          <!-- El estado de carga va en el aviso sobre la tabla (con filas) o en la zona de carga (sin filas). -->
          <div class="busy-line d-flex align-center text-caption">{{ providerRuleText }}</div>
        </div>

        <div class="d-flex flex-wrap justify-end">
          <!-- Un botón deshabilitado no recibe clic: el span lo recoge y sacude el almacén si es lo que falta. -->
          <template v-if="importFinished">
            <v-btn small color="primary" class="mr-2 mb-2" @click="closeImport">
              <v-icon left>mdi-close</v-icon>
              Cerrar
            </v-btn>
            <v-btn small color="btn-save" class="mr-2 mb-2" :disabled="loading" @click="clearImport">
              <v-icon left>mdi-file-upload-outline</v-icon>
              Importar otro archivo
            </v-btn>
          </template>
          <span v-else @click="nudgeWarehouse">
            <v-btn small color="btn-save" class="mr-2 mb-2" :disabled="saveDisabled" @click="confirmImport">
              <v-icon left>mdi-content-save</v-icon>
              Guardar
            </v-btn>
          </span>
          <v-btn small color="btnclean" class="mr-2 mb-2" :disabled="loading || !rows.length" @click="askClear">
            <v-icon left>mdi-broom</v-icon>
            Limpiar
          </v-btn>
          <v-btn small class="mr-2 mb-2 btn-save" :disabled="loading" @click="downloadTemplate">
            <v-icon left>mdi-download</v-icon>
            Plantilla
          </v-btn>
          <v-btn small color="primary" class="mb-2" :disabled="loading" @click="pickExcel">
            <v-icon left>mdi-file-excel</v-icon>
            Cargar Excel
          </v-btn>
          <input ref="excelInput" type="file" accept=".xlsx,.xls" class="d-none" @change="handleFileChange" />
        </div>
      </div>
    </v-card>

    <section v-if="rows.length" class="summary" aria-label="Resumen de la importación">
      <!-- Los íconos se dibujan una vez; al cambiar el tono (:key) se vuelven a dibujar. -->
      <div class="stats">
        <div v-for="stat in stats" :key="stat.key" class="stat" :class="`tone-${stat.tone}`">
          <span class="stat__icon">
            <LottiePlayer :key="`${stat.key}-${stat.tone}`" :animation="stat.animation" :loop="false" class="stat__art" />
          </span>
          <span class="stat__text">
            <span class="stat__value">{{ stat.value }}</span>
            <span class="stat__label">{{ stat.label }}</span>
          </span>
        </div>
      </div>

      <div v-if="previousImport" class="banner tone-warning" role="status">
        <span class="banner__icon"><LottiePlayer :animation="icons.alert" :loop="false" class="banner__art" /></span>
        <div class="banner__text">
          <div class="banner__title">Este archivo ya se importó antes</div>
          <div class="banner__detail">{{ previousImportText }}</div>
        </div>
      </div>

      <div v-if="result" class="banner tone-success" role="status">
        <span class="banner__icon"><LottiePlayer :animation="icons.check" :loop="false" class="banner__art" /></span>
        <div class="banner__text">
          <div class="banner__title">{{ result.created }} {{ result.created === 1 ? "producto importado" : "productos importados" }}</div>
          <div class="banner__detail">Con su precio, proveedor, código de barra y existencia inicial.</div>
        </div>
        <!-- Todo lo cargado ya se importó: se ofrece volver a la pantalla inicial (hace lo mismo que Limpiar). -->
        <span v-if="importFinished" class="banner__action">
          <v-btn color="primary" class="white--text mr-2" @click="closeImport">
            <v-icon left>mdi-close</v-icon>
            Cerrar
          </v-btn>
          <v-btn color="green darken-3" class="white--text" @click="clearImport">
            <v-icon left>mdi-file-upload-outline</v-icon>
            Importar otro archivo
          </v-btn>
        </span>
      </div>
      <div v-if="review" :key="review.tone" class="banner" :class="`tone-${review.tone}`" role="status">
        <span class="banner__icon"><LottiePlayer :animation="review.tone === 'success' ? icons.check : icons.alert" :loop="false" class="banner__art" /></span>
        <div class="banner__text">
          <div class="banner__title">{{ review.title }}</div>
          <div class="banner__detail">{{ review.detail }}</div>
        </div>
        <div v-if="bulkGroups.length" class="review__bulk">
          <div class="review__bulk-title">Errores que se repiten: elija el valor correcto y aplíquelo a todas esas filas</div>
          <div v-for="group in bulkGroups" :key="group.key" class="review__bulk-row">
            <span class="review__bulk-label">{{ group.label }} · <strong>{{ group.count }} filas</strong></span>
            <v-autocomplete
              :value="bulkChoice[group.key]"
              :items="bulkItems(group.field)"
              item-text="text"
              item-value="value"
              label="Usar"
              class="review__bulk-select"
              dense
              outlined
              hide-details
              @change="$set(bulkChoice, group.key, $event)"
            ></v-autocomplete>
            <v-btn small color="green darken-3" class="white--text" :disabled="!bulkChoice[group.key]" @click="applyBulk(group)">Aplicar a {{ group.count }}</v-btn>
          </div>
        </div>
        <div class="review__actions">
          <v-btn small color="green darken-3" class="white--text" @click="view = 'all'">{{ review.continueLabel }}</v-btn>
          <v-btn v-if="duplicateRows.length" small text color="red darken-3" :disabled="loading" @click="askRemoveDuplicates">{{ removeDuplicatesLabel }}</v-btn>
        </div>
      </div>
      <!-- Ocupado (leer, crear segmentaciones, guardar): el aviso se ve también en el paso de revisión, junto a la tabla. -->
      <div v-if="banner && (!reviewing || banner.busy)" :key="banner.key" class="banner" :class="`tone-${banner.tone}`" role="status">
        <span class="banner__icon">
          <LottiePlayer :animation="banner.busy ? loaderAnimation : banner.tone === 'success' ? icons.check : icons.alert" :loop="!!banner.busy" class="banner__art" />
        </span>
        <div class="banner__text">
          <div class="banner__title">{{ banner.title }}</div>
          <div class="banner__detail">{{ banner.detail }}</div>
        </div>
        <!-- Verde cuando se puede importar; gris cuando falta algo (el aviso de arriba dice qué). -->
        <span class="banner__action" @click="nudgeWarehouse">
          <v-btn v-if="banner.key === 'errors' && reviewEnabled" small text class="mr-2" @click.stop="openReview">Ver solo los {{ attentionText }} que necesitan atención</v-btn>
          <v-btn v-if="banner.key === 'errors' && duplicateRows.length" small text color="red darken-3" class="mr-2" @click.stop="askRemoveDuplicates">{{ removeDuplicatesLabel }}</v-btn>
          <v-btn color="green darken-3" class="white--text" :disabled="saveDisabled && !loading" :loading="loading" @click="confirmImport">
            <v-icon left>mdi-content-save</v-icon>
            Guardar
          </v-btn>
        </span>
      </div>

      <v-row dense align="start">
        <v-col v-if="!reviewing" cols="12" md="4">
          <v-autocomplete
            v-model="warehouse"
            :items="warehouses"
            item-text="Warehouse"
            item-value="ID_Warehouse"
            label="Almacén para la existencia inicial"
            :error-messages="warehouseMessages"
            :class="{ 'shake-y': shaking }"
            hide-details="auto"
            outlined
            dense
            clearable
            @change="warehouseError = ''"
            @animationend.native="shaking = false"
          ></v-autocomplete>
        </v-col>
        <v-col cols="12" md="8" class="text-md-right">
          <v-btn v-if="newSegmentationCount" small color="primary" :disabled="loading" @click="confirmCreateSegmentation">
            <v-icon left>mdi-plus</v-icon>
            Crear {{ newSegmentationCount }} segmentaciones nuevas
          </v-btn>
        </v-col>
      </v-row>
    </section>

    <v-row v-if="!rows.length">
      <v-col cols="12" md="7">
        <section class="panel upload-panel">
          <h3 class="text-h6 mb-1">Cargar archivo</h3>
          <p class="text-body-2 mb-4 panel-muted">Excel .xlsx o .xls, hasta {{ maxRows }} productos por importación.</p>
          <v-btn color="primary" class="mb-5" :disabled="loading" @click="downloadTemplate">
            <v-icon left>mdi-download</v-icon>
            Descargar plantilla
          </v-btn>

          <div
            class="dropzone"
            :class="{ 'dropzone--over': dragging, 'dropzone--busy': loading }"
            role="button"
            tabindex="0"
            :aria-disabled="loading ? 'true' : 'false'"
            aria-label="Cargar archivo Excel: arrastre el archivo aquí o presione Enter para buscarlo en su equipo"
            @click="openPicker"
            @keydown.enter.prevent="openPicker"
            @keydown.space.prevent="openPicker"
            @dragover.prevent="dragging = !loading"
            @dragleave.prevent="dragging = false"
            @drop.prevent="onDrop"
          >
            <!-- :key reinicia el reproductor al cambiar de animación (nube <-> carga). -->
            <LottiePlayer
              :key="loading ? 'loader' : 'cloud'"
              :animation="loading ? loaderAnimation : cloudAnimation"
              :speed="dragging ? 2.4 : 1"
              class="dropzone__art"
            />
            <div v-if="loading" class="dropzone__title" role="status">{{ busyText }}</div>
            <template v-else>
              <div class="dropzone__title">{{ dragging ? "Suelte el archivo para cargarlo" : "Arrastre su archivo aquí" }}</div>
              <div class="text-body-2 panel-muted">o <span class="dropzone__link">búsquelo en su equipo</span></div>
            </template>
          </div>
        </section>
      </v-col>

      <v-col cols="12" md="5">
        <section class="panel mb-4">
          <h3 class="text-h6 mb-3">Cómo funciona</h3>
          <ol class="steps">
            <li v-for="(step, index) in howItWorks" :key="index">
              <span class="steps__number">{{ index + 1 }}</span>
              <span class="text-body-2">{{ step }}</span>
            </li>
          </ol>
        </section>
        <section class="panel">
          <h3 class="text-h6 mb-3">Tenga en cuenta</h3>
          <ol class="steps">
            <li v-for="(tip, index) in tips" :key="index">
              <span class="steps__number">{{ index + 1 }}</span>
              <span class="text-body-2">{{ tip }}</span>
            </li>
          </ol>
        </section>
      </v-col>
    </v-row>

    <DxDataGrid
      v-if="rows.length"
      ref="grid"
      :class="{ 'is-locked': loading }"
      :data-source="rows"
      :filter-value="gridFilter"
      key-expr="_rowId"
      :show-borders="true"
      :column-auto-width="true"
      :selected-row-keys="selectedRowKeys"
      @selection-changed="onSelectionChanged"
      @editor-preparing="onEditorPreparing"
      @row-updating="onRowUpdating"
      @row-updated="onRowUpdated"
      @cell-prepared="onCellPrepared"
    >
      <DxEditing :allow-updating="isRowEditable" mode="cell" />
      <DxSelection mode="multiple" show-check-boxes-mode="always" />
      <DxPaging :page-size="50" />
      <DxHeaderFilter :visible="true" />

      <!-- Oculta: el filtro del paso de revisión (filter-value) exige que el campo sea una columna. -->
      <DxColumn data-field="inReview" :visible="false" :allow-editing="false" :show-in-column-chooser="false" :allow-header-filtering="false" />
      <DxColumn data-field="status" caption="Estado" :min-width="150" :allow-editing="false" cell-template="status-cell" />
      <DxColumn
        name="detail"
        caption="Detalle"
        cell-template="detail-cell"
        :allow-editing="false"
        :allow-sorting="false"
        :allow-header-filtering="false"
        :width="300"
        css-class="detail-cell"
      />
      <DxColumn data-field="excelRow" caption="Fila" :allow-editing="false" :allow-header-filtering="false" />
      <DxColumn data-field="name" caption="Nombre*" :allow-header-filtering="false" />
      <DxColumn data-field="comercialName" caption="Nombre comercial" :allow-header-filtering="false" />
      <DxColumn data-field="description" caption="Descripción" :allow-header-filtering="false" />
      <DxColumn data-field="measureUnit" caption="Unidad de medida*" :calculate-display-value="display.measureUnit">
        <DxLookup :data-source="units" display-expr="Valores" value-expr="Valores" />
      </DxColumn>
      <DxColumn data-field="price" caption="Precio de venta*" data-type="number" :allow-header-filtering="false" />
      <DxColumn data-field="cost" caption="Costo" data-type="number" :allow-header-filtering="false" />
      <DxColumn data-field="stock" caption="Existencia" data-type="number" :allow-header-filtering="false" />
      <DxColumn data-field="barcode" caption="Código de barra" :allow-header-filtering="false" />
      <DxColumn data-field="aditionalType" caption="Dato adicional">
        <DxLookup :data-source="aditionalTypes" display-expr="text" value-expr="value" />
      </DxColumn>
      <DxColumn data-field="family" caption="Familia" :calculate-display-value="display.family">
        <DxLookup :data-source="familyOptions" display-expr="name" value-expr="id" />
      </DxColumn>
      <DxColumn data-field="category" caption="Categoría" :calculate-display-value="display.category">
        <DxLookup :data-source="categoryOptions" display-expr="name" value-expr="id" />
      </DxColumn>
      <DxColumn data-field="subcategory" caption="Subcategoría" :calculate-display-value="display.subcategory">
        <DxLookup :data-source="subcategoryOptions" display-expr="name" value-expr="id" />
      </DxColumn>
      <DxColumn data-field="idProvider" caption="Proveedor" :calculate-display-value="display.idProvider">
        <DxLookup :data-source="providerOptions" display-expr="label" value-expr="ID_Provider" />
      </DxColumn>

      <template #status-cell="{ data }">
        <v-chip x-small dark :color="rowState(data.data).color" :title="rowErrorText(data.data)" v-bind="fixer(data.data, 'status').attrs" v-on="fixer(data.data, 'status').on">
          <v-icon left x-small>{{ rowState(data.data).icon }}</v-icon>
          {{ rowState(data.data).label }}
        </v-chip>
      </template>
      <!-- Plantilla y no calculate-cell-value: el valor calculado queda en caché y no cambia al guardar sin recargar. -->
      <template #detail-cell="{ data }">
        <span v-bind="fixer(data.data, 'detail').attrs" v-on="fixer(data.data, 'detail').on">{{ rowDetail(data.data) }}</span>
      </template>
    </DxDataGrid>

    <!-- Tras importar, con filas sin corregir: cerrar la importación (igual que Limpiar), con confirmación. -->
    <div v-if="reviewing && hasImported" class="d-flex justify-end mt-5">
      <v-btn color="btnclean" :disabled="loading" @click="ignoreDialog = true">
        <v-icon left>mdi-close-circle-outline</v-icon>
        Ignorar y cerrar esta importación
      </v-btn>
    </div>

    <!-- Mismo Guardar al pie de la tabla: ahí termina la revisión y ahí se ve que ya se habilitó. -->
    <div v-if="rows.length && !reviewing" class="d-flex justify-end mt-5">
      <template v-if="importFinished">
        <v-btn color="primary" class="white--text mr-2" @click="closeImport">
          <v-icon left>mdi-close</v-icon>
          Cerrar
        </v-btn>
        <v-btn color="green darken-3" class="white--text" :disabled="loading" @click="clearImport">
          <v-icon left>mdi-file-upload-outline</v-icon>
          Importar otro archivo
        </v-btn>
      </template>
      <span v-else @click="nudgeWarehouse">
        <v-btn color="green darken-3" class="white--text" :disabled="saveDisabled && !loading" :loading="loading" @click="confirmImport">
          <v-icon left>mdi-content-save</v-icon>
          Guardar
        </v-btn>
      </span>
    </div>

    <!-- key en cada diálogo: sin key, Vue reutiliza la instancia de otro diálogo y se queda con su slot "cell". -->
    <ImportConfirmDialog
      key="import-dialog"
      v-model="importDialog"
      tone="success"
      icon="mdi-content-save-outline"
      :title="importTitle"
      subtitle="Se crearán con su precio, proveedor, código de barra y existencia inicial."
      confirm-label="Importar"
      :summary="importSummary"
      :columns="importColumns"
      :rows="importRows"
      :total="selectedValidRows.length"
      :notice="importNotice"
      @confirm="runImport"
    >
      <template #cell="{ row, column }">
        <template v-if="column.key === 'name'">
          <strong>{{ row.name }}</strong>
          <div v-if="row.detail" class="confirm-sub">{{ row.detail }}</div>
        </template>
        <template v-else>{{ row[column.key] }}</template>
      </template>
    </ImportConfirmDialog>

    <ImportConfirmDialog
      key="leave-dialog"
      v-model="leaveDialog"
      tone="warning"
      :icon="leaveIcon"
      :title="leaveTitle"
      :subtitle="leaveSubtitle"
      :confirm-label="leaveConfirmLabel"
      :summary="leaveSummary"
      @confirm="runLeave"
    ></ImportConfirmDialog>

    <ImportConfirmDialog
      key="ignore-dialog"
      v-model="ignoreDialog"
      tone="warning"
      icon="mdi-close-circle-outline"
      title="Ignorar y cerrar esta importación"
      :subtitle="ignoreSubtitle"
      confirm-label="Ignorar y cerrar"
      :summary="ignoreSummary"
      :columns="ignoreColumns"
      :rows="ignoreRows"
      :total="ignoreTotal"
      @confirm="runIgnore"
    >
      <template #cell="{ row, column }">
        <strong v-if="column.key === 'name'">{{ row.name }}</strong>
        <template v-else>{{ row[column.key] }}</template>
      </template>
    </ImportConfirmDialog>

    <ImportConfirmDialog
      key="segmentation-dialog"
      v-model="segmentationDialog"
      tone="info"
      icon="mdi-shape-plus-outline"
      title="Crear segmentaciones nuevas"
      :subtitle="segmentationSubtitle"
      :confirm-label="segmentationConfirmLabel"
      :secondary-label="segmentationSecondaryLabel"
      :summary="segmentationSummary"
      :columns="segmentationColumns"
      :rows="segmentationRows"
      :total="segmentationTotal"
      @confirm="runCreateSegmentation"
      @secondary="skipSegmentations"
    >
      <template #cell="{ row, column }">
        <template v-if="column.key === 'news'">
          <span v-for="item in row.news" :key="item.label" class="confirm-tag">{{ item.label }} · <strong>{{ item.name }}</strong></span>
        </template>
        <strong v-else-if="column.key === 'name'">{{ row.name }}</strong>
        <template v-else>{{ row[column.key] }}</template>
      </template>
    </ImportConfirmDialog>

    <ImportRowDialog
      v-model="rowDialog" :title="`Corregir fila ${editingRow ? editingRow.excelRow : ''}`" :subtitle="editingRow ? editingRow.name || 'Sin nombre' : ''" :row="formRow"
      :fields="rowFields" :groups="rowGroups" :items-for="itemsForField" :validate="validateDraft" :has-next="rowDialog && !!nextErrorRow(editingRow)"
      remove-label="Quitar fila" @apply="applyRow($event)" @apply-next="applyRow($event, true)" @remove="removeRows([editingRow])"
    />

    <alerts v-if="alert.show" :properties="alert" @close_alert="closeAlert" @accept_alert="acceptAlert"></alerts>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import saveAs from "file-saver";
import { DxDataGrid, DxColumn, DxEditing, DxLookup, DxPaging, DxSelection, DxHeaderFilter } from "devextreme-vue/data-grid";
import Alerts from "@/components/Alerts";
import LottiePlayer from "@/components/LottiePlayer";
import ImportConfirmDialog from "@/components/inventory/ImportConfirmDialog";
import ImportRowDialog from "@/components/inventory/ImportRowDialog";
import cloudAnimation from "@/assets/lottie/cloud-upload.json";
import loaderAnimation from "@/assets/lottie/loader.json";
import sheetIcon from "@/assets/lottie/icon-sheet.json";
import checkIcon from "@/assets/lottie/icon-check.json";
import alertIcon from "@/assets/lottie/icon-alert.json";
import { getUUIDv4 } from "@/helpers/misc";

const MAX_ROWS = 10000; // igual que el DTO del backend
// La plantilla trae listas desplegables solo en las primeras filas (una por celda: 10 000 filas la volverían pesada); el resto se valida al cargar.
const TEMPLATE_VALIDATION_ROWS = 1000;
// Los diálogos de confirmación listan solo las primeras filas (10 000 filas en una tabla del navegador la congelarían).
const PREVIEW_ROWS = 200;
// Desde 20 filas en total, si alguna necesita atención, se muestra primero un paso de revisión solo con ellas: así no hay que buscarlas entre la lista.
const REVIEW_MIN_ROWS = 20;
// Errores de "elegir del catálogo" que se repiten y se pueden corregir de una vez para todas las filas.
const BULK_FIELDS = ["measureUnit", "idProvider", "aditionalType"];
// Huella del archivo cargado (nombre, precio, código, unidad, existencia) para avisar si ya se importó antes desde este navegador.
const RECENT_KEY = "innovabiz.importV2.recent";
const hashRows = (rows) => {
  const text = rows.map((row) => `${row.name}|${row.comercialName}|${row.price}|${row.barcode}|${row.raw.measureUnit}|${row.raw.stock}`).join("\n");
  let hash = 2166136261;
  for (let i = 0; i < text.length; i++) hash = Math.imul(hash ^ text.charCodeAt(i), 16777619) >>> 0;
  return `${rows.length}-${hash.toString(16)}`;
};
const WAREHOUSE_REQUIRED = "Seleccione un almacén para la existencia inicial.";
// Columna Estado. Tonos con contraste ≥ 4.5:1 frente a texto blanco en chips x-small (success/warning/grey del tema no llegan).
const STATES = {
  imported: { label: "Importada", icon: "mdi-check-circle", color: "green darken-3" },
  ready: { label: "Lista", icon: "mdi-check", color: "blue darken-2" },
  unselected: { label: "Sin seleccionar", icon: "mdi-minus-circle-outline", color: "grey darken-2" },
  errors: { label: "Con errores", icon: "mdi-alert-circle", color: "error" },
  new: { label: "Nueva", icon: "mdi-plus-circle-outline", color: "deep-orange darken-4" },
};
const formatCount = (count) => count.toLocaleString(); // separador de miles según el idioma del navegador
const plural = (count, one, many) => `${formatCount(count)} ${count === 1 ? one : many}`;
const formatAmount = (value) => (value === null || value === undefined || value === "" ? "—" : Number(value).toLocaleString("es", { maximumFractionDigits: 2 }));
// Columnas de los diálogos de confirmación.
const IMPORT_COLUMNS = [
  { key: "excelRow", label: "Fila" },
  { key: "name", label: "Producto" },
  { key: "measureUnit", label: "Unidad" },
  { key: "price", label: "Precio", numeric: true },
  { key: "stock", label: "Existencia", numeric: true },
];
const IGNORE_COLUMNS = [
  { key: "excelRow", label: "Fila" },
  { key: "name", label: "Producto" },
  { key: "detail", label: "Problema" },
];
const SEGMENTATION_COLUMNS = [
  { key: "excelRow", label: "Fila" },
  { key: "name", label: "Producto" },
  { key: "news", label: "Segmentación nueva" },
];
// Pantalla vacía: el flujo real en 4 pasos y las reglas de la plantilla (hoja Instrucciones).
const HOW_IT_WORKS = [
  "Descargue la plantilla y llene la hoja Productos. Las columnas con * son obligatorias.",
  "Cargue el archivo: arrástrelo a la zona punteada o búsquelo en su equipo.",
  "Revise la tabla. Las celdas en rojo tienen errores y se corrigen aquí mismo, sin volver al Excel.",
  "Si carga existencia, elija el almacén y presione Guardar.",
];
const TIPS = [
  `Máximo ${formatCount(MAX_ROWS)} productos por importación.`,
  "Precio, costo y existencia: solo números, con máximo 2 decimales (ej. 1250.50).",
  "Unidad, familia, categoría, subcategoría y proveedor: use los nombres de la hoja Catalogos de la plantilla.",
  "El nombre no puede repetirse en el archivo ni ser igual al de un producto ya creado.",
];
// Única fuente de encabezados: plantilla y lector usan la misma lista.
const COLUMNS = [
  { header: "Nombre*", key: "name", width: 34 },
  { header: "Nombre comercial", key: "comercialName", width: 28 },
  { header: "Descripción", key: "description", width: 34 },
  { header: "Unidad de medida*", key: "measureUnit", width: 20 },
  { header: "Precio de venta*", key: "price", width: 16 },
  { header: "Costo", key: "cost", width: 12 },
  { header: "Existencia", key: "stock", width: 12 },
  { header: "Código de barra", key: "barcode", width: 20 },
  { header: "Dato adicional", key: "aditionalType", width: 16 },
  { header: "Familia", key: "family", width: 20 },
  { header: "Categoría", key: "category", width: 20 },
  { header: "Subcategoría", key: "subcategory", width: 20 },
  { header: "Proveedor (opcional)", key: "provider", width: 28 },
  { header: "NIT Proveedor (opcional)", key: "nit", width: 22 },
];
const REQUIRED_HEADERS = ["name", "measureUnit", "price"];
const FIELD_LABELS = { name: "Nombre", comercialName: "Nombre comercial", description: "Descripción", measureUnit: "Unidad de medida", price: "Precio de venta", cost: "Costo", stock: "Existencia", barcode: "Código de barra", aditionalType: "Dato adicional", family: "Familia", category: "Categoría", subcategory: "Subcategoría", idProvider: "Proveedor" };
const AMOUNTS = ["price", "cost", "stock"]; // en el formulario se escriben como texto y se convierten al aplicar
const ROW_GROUPS = [ // formulario de corrección de una fila: los campos de FIELD_LABELS en 3 bloques
  { title: "Producto", keys: ["name", "comercialName", "description", "measureUnit", "aditionalType"] },
  { title: "Precio y existencia", keys: ["price", "cost", "stock", "barcode"] },
  { title: "Segmentación y proveedor", keys: ["family", "category", "subcategory", "idProvider"] },
];
const SEGMENTS = [
  { field: "family", list: "families", parentField: null, parentKey: null },
  { field: "category", list: "categories", parentField: "family", parentKey: "id_family" },
  { field: "subcategory", list: "subcategories", parentField: "category", parentKey: "id_cat" },
];
// Intencional: el sanitizer reemplaza caracteres de control del Excel por espacio.
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\u0000-\u001F\u007F]+/g;
const FORMULA_START = /^\s*[=+\-@]/;
const MAX_AMOUNT = 99999999.99;

const cleanText = (value) => String(value ?? "").replace(CONTROL_CHARS, " ").trim();
const normalize = (value) => cleanText(value).normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, " ").toLowerCase();
const normalizeHeader = (value) => normalize(value).replace(/\*/g, "").replace(/\(opcional\)/g, "").trim();
const normalizeNit = (value) => cleanText(value).replace(/[^0-9a-z]/gi, "").toLowerCase();
const isTruthyFlag = (value) => value === true || value === 1 || value === "1" || value === "true";
// Excel guarda el resultado de fórmulas con ruido binario (=A2*1.13 -> 11.299999999999999): se toma como 11.3.
// Un valor con más de 2 decimales reales (1.239) se deja tal cual y validateRow lo marca.
const cleanExcelNumber = (value) => { const cents = Math.round(value * 100); return Math.abs(value * 100 - cents) <= Math.abs(value * 100) * Number.EPSILON * 8 ? cents / 100 : value; };

export default {
  name: "ManageProductImportV2",
  components: { DxDataGrid, DxColumn, DxEditing, DxLookup, DxPaging, DxSelection, DxHeaderFilter, Alerts, LottiePlayer, ImportConfirmDialog, ImportRowDialog },
  data() {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    return {
      loading: false,
      busyText: "",
      dragging: false,
      shaking: false,
      reviewEnabled: false,
      view: "all", // "attention": solo filas que necesitan atención (paso de revisión) | "all"
      importDialog: false,
      ignoreDialog: false,
      leaveDialog: false,
      leaveAction: "close", // qué confirma el diálogo: "close" (salir de la ventana) | "replace" (cargar otro Excel) | "clear" (Limpiar) | "duplicates" (quitar las filas con nombre repetido)
      bulkChoice: {},
      fileHash: "",
      previousImport: null,
      ignoreColumns: IGNORE_COLUMNS,
      segmentationDialog: false,
      segmentationThenImport: false,
      importColumns: IMPORT_COLUMNS,
      segmentationColumns: SEGMENTATION_COLUMNS,
      maxRows: formatCount(MAX_ROWS),
      howItWorks: HOW_IT_WORKS,
      tips: TIPS,
      rows: [],
      selectedRowKeys: [],
      rowDialog: false, editingRow: null, // formulario de corrección de una fila: abierto, y la fila que se corrige
      units: [],
      families: [],
      categories: [],
      subcategories: [],
      providers: [],
      warehouses: [],
      warehouse: null,
      warehouseError: "",
      result: null,
      providerRequired: !isTruthyFlag(user.nomergeprodwithprovs),
      aditionalTypes: [
        { value: "", text: "(ninguno)" },
        { value: "Medicamento", text: "Medicamento" },
        { value: "Vacuna", text: "Vacuna" },
      ],
      alert: { type: "success", show: false, header: "", body: "", options: null },
    };
  },
  computed: {
    providerRuleText() {
      return this.providerRequired
        ? "Proveedor obligatorio (solo de inventario): este negocio relaciona productos con proveedores."
        : "Proveedor opcional en este negocio (solo de inventario).";
    },
    familyOptions() {
      return this.withNone(this.families);
    },
    categoryOptions() {
      return this.withNone(this.categories);
    },
    subcategoryOptions() {
      return this.withNone(this.subcategories);
    },
    // Opción vacía explícita: elegirla es un cambio real (null -> ""), así se descarta el texto del Excel.
    providerOptions() {
      return [{ ID_Provider: "", label: "(sin proveedor)" }, ...this.providers.map((provider) => ({
        ID_Provider: provider.ID_Provider,
        label: `${provider.ProviderName || provider.ProviderLegalName || provider.ID_Provider}${provider.ProviderLegalNIT ? ` · ${provider.ProviderLegalNIT}` : ""}`,
      }))];
    },
    // Los proveedores como opciones del formulario: se arman una vez (son miles) y no con cada tecla.
    providerItems() {
      return this.providerOptions.map(({ ID_Provider: value, label: text }) => ({ text, value }));
    },
    // Con lo que el formulario de corrección arranca el borrador de la fila que se corrige.
    formRow() {
      const row = this.editingRow;
      return row && Object.fromEntries(Object.keys(FIELD_LABELS).map((key) => [key, this.formValue(row, key)]));
    },
    selectedValidRows() {
      const selected = new Set(this.selectedRowKeys);
      return this.rows.filter((row) => selected.has(row._rowId) && row.status === "Lista");
    },
    invalidCount() {
      return this.rows.filter((row) => row.status === "Con errores").length;
    },
    needsWarehouse() {
      return this.selectedValidRows.some((row) => row.stock > 0);
    },
    warehouseMissing() {
      return this.needsWarehouse && !this.warehouse;
    },
    rowCounts() {
      let ready = 0;
      let errors = 0;
      let fresh = 0;
      this.rows.forEach((row) => {
        if (row.saved) return;
        if (row.status === "Lista") ready++;
        else if (row.status === "Con errores") errors++;
        else if (row.status === "Nueva") fresh++;
      });
      return { ready, errors, fresh, attention: errors + fresh };
    },
    // Paso de revisión: solo las filas que necesitan atención (con errores o por crear segmentaciones).
    attentionText() {
      return formatCount(this.rowCounts.attention);
    },
    // La lista de revisión son las filas que entraron con problemas y se queda así aunque se corrijan, para ver qué se arregla.
    inReviewCount() {
      return this.rows.filter((row) => row.inReview && !row.saved).length;
    },
    reviewing() {
      return this.reviewEnabled && this.view === "attention" && this.inReviewCount > 0;
    },
    gridFilter() {
      return this.reviewing ? ["inReview", "=", true] : null;
    },
    review() {
      if (!this.reviewing) return null;
      const { ready, errors, fresh, attention } = this.rowCounts;
      const continueLabel = ready === 0 ? "Ver todas las filas" : ready === 1 ? "Continuar con el producto listo" : `Continuar con los ${formatCount(ready)} listos`;
      if (!attention) {
        return {
          tone: "success",
          title: `Todo corregido: ${plural(ready, "producto listo", "productos listos")} para importar`,
          detail: "Las filas corregidas se quedan en la lista para que las revise. Continúe cuando termine.",
          continueLabel,
        };
      }
      const breakdown = [errors ? `${formatCount(errors)} con errores` : "", fresh ? `${formatCount(fresh)} por crear segmentaciones` : ""].filter(Boolean).join(" · ");
      const others = ready === 0 ? "Ninguno está listo todavía." : ready === 1 ? "El otro está listo." : `Los otros ${formatCount(ready)} ya están listos.`;
      return {
        tone: "warning",
        title: `${plural(attention, "producto necesita", "productos necesitan")} su atención`,
        detail: `${breakdown}. ${others} Corríjalos aquí mismo en la tabla; los que no corrija no se importarán.`,
        continueLabel,
      };
    },
    stats() {
      const errors = this.invalidCount;
      const ready = this.selectedValidRows.length;
      return [
        { key: "loaded", label: "Cargadas", value: formatCount(this.rows.length), tone: "info", animation: this.icons.sheet },
        { key: "errors", label: "Con errores", value: formatCount(errors), tone: errors ? "danger" : "muted", animation: this.icons.alert },
        { key: "ready", label: "Seleccionadas y válidas", value: formatCount(ready), tone: ready ? "success" : "muted", animation: this.icons.check },
      ];
    },
    // Un solo mensaje sobre lo que falta por importar: qué pasa y qué hacer. Sin filas pendientes no hay mensaje.
    banner() {
      if (this.loading && this.rows.length) return { key: "busy", busy: true, tone: "info", title: this.busyText, detail: "Espere a que termine; la tabla queda bloqueada mientras tanto." };
      if (!this.rows.some((row) => !row.saved)) return null;
      const errors = this.invalidCount;
      const ready = this.selectedValidRows.length;
      if (errors) {
        let lead = ready ? `${plural(ready, "lista para guardar", "listas para guardar")}. ` : "";
        if (ready > MAX_ROWS) lead = `${formatCount(ready)} listas, pero solo se importan ${formatCount(MAX_ROWS)} por vez: desmarque filas hasta quedar en ${formatCount(MAX_ROWS)}. `;
        return { key: "errors", tone: "danger", title: plural(errors, "fila con errores", "filas con errores"), detail: `${lead}Las celdas en rojo se corrigen aquí mismo, sin volver al Excel.` };
      }
      if (!ready) {
        const detail = this.newSegmentationCount ? "Cree las segmentaciones nuevas para poder seleccionar esas filas." : "Marque en la tabla las filas que quiere importar.";
        return { key: "none", tone: "warning", title: "No hay filas seleccionadas y válidas.", detail };
      }
      if (ready > MAX_ROWS) return { key: "limit", tone: "warning", title: `Máximo ${formatCount(MAX_ROWS)} productos por importación.`, detail: "Desmarque filas e impórtelas en lotes." };
      // Guardar solo importa filas "Lista": las "Nueva" (segmentación inexistente) esperan al botón "Crear segmentaciones nuevas".
      const fresh = this.rows.filter((row) => row.status === "Nueva").length;
      const note = fresh ? ` ${plural(fresh, "fila usa", "filas usan")} segmentaciones nuevas: presione "Crear ${this.newSegmentationCount} segmentaciones nuevas" para poder importar${fresh === 1 ? "la" : "las"}.` : "";
      const title = plural(ready, "producto listo para importar", "productos listos para importar");
      if (this.warehouseMissing) return { key: "warehouse", tone: "warning", title, detail: `Elija el almacén de la existencia inicial para poder guardar.${note}` };
      return { key: fresh ? "ready-new" : "ready", tone: fresh ? "warning" : "success", title, detail: `Presione Guardar para crearlos.${note}` };
    },
    // Error del servidor (si hay) o el aviso de que falta elegir almacén; se muestra bajo el propio campo.
    warehouseMessages() {
      if (this.warehouseError) return [this.warehouseError];
      return this.warehouseMissing ? [WAREHOUSE_REQUIRED] : [];
    },
    // Hay filas cargadas que todavía no se importaron: salir las perdería.
    hasUnsavedWork() {
      return this.rows.some((row) => !row.saved);
    },
    leaveTitle() {
      if (this.leaveAction === "duplicates") return `¿${this.removeDuplicatesLabel}?`;
      return { replace: "¿Reemplazar el archivo cargado?", clear: "¿Limpiar la importación?" }[this.leaveAction] || "¿Salir sin importar?";
    },
    leaveIcon() {
      return { replace: "mdi-file-replace-outline", clear: "mdi-broom", duplicates: "mdi-delete-sweep-outline" }[this.leaveAction] || "mdi-exit-run";
    },
    leaveConfirmLabel() {
      return { replace: "Reemplazar y elegir archivo", clear: "Limpiar y descartar", duplicates: "Quitar" }[this.leaveAction] || "Salir y descartar";
    },
    leaveSubtitle() {
      if (this.leaveAction === "duplicates") return "Se quitan de esta importación y no se importan; el Excel no cambia. De los nombres repetidos en el archivo se queda la primera fila; las que ya existen en el negocio se quitan todas.";
      const left = this.rows.filter((row) => !row.saved).length;
      if (!left) return "Se reemplazará el archivo cargado por el que elija. Lo ya importado no se deshace.";
      return `${plural(left, "producto sin importar se perderá", "productos sin importar se perderán")}. Los cambios que hizo en la tabla no se guardan.`;
    },
    leaveSummary() {
      if (this.leaveAction === "duplicates") return [];
      const saved = this.rows.filter((row) => row.saved).length;
      return saved ? [{ icon: "mdi-check-circle-outline", text: `${plural(saved, "producto ya importado", "productos ya importados")}: no se deshace` }] : [];
    },
    previousImportText() {
      const previous = this.previousImport;
      if (!previous) return "";
      return `Parece idéntico al que importó el ${new Date(previous.at).toLocaleString()} (${plural(previous.count, "producto", "productos")}). Si lo importa otra vez, los que ya existen se marcarán con nombre repetido al guardar.`;
    },
    // Errores que se repiten en 2 o más filas (misma unidad inexistente, proveedor obligatorio vacío…): una sola corrección para todas.
    bulkGroups() {
      if (!this.reviewing) return [];
      const groups = new Map();
      this.rows.forEach((row) => {
        if (row.saved) return;
        BULK_FIELDS.forEach((field) => {
          if (!row.errors[field]) return;
          const source = this.bulkSource(row, field);
          const key = `${field}|${normalize(source)}`;
          const group = groups.get(key) || { key, field, source, rows: [] };
          group.rows.push(row);
          groups.set(key, group);
        });
      });
      return [...groups.values()]
        .filter((group) => group.rows.length >= 2)
        .sort((a, b) => b.rows.length - a.rows.length)
        .slice(0, 5)
        .map((group) => ({ ...group, count: group.rows.length, label: `${FIELD_LABELS[group.field]} ${group.source === "(vacío)" ? "vacío" : `«${group.source}»`}` }));
    },
    // Filas sin importar con nombre repetido: en el archivo o ya existente en el negocio (lo dice el servidor al guardar).
    duplicateRows() {
      return this.rows.filter((row) => !row.saved && (row.duplicateOf || /^Ya existe un producto/.test(row.serverErrors.name || "")));
    },
    removeDuplicatesLabel() {
      const count = this.duplicateRows.length;
      return `Quitar ${plural(count, "fila", "filas")} con nombre repetido`;
    },
    hasImported() {
      return this.rows.some((row) => row.saved);
    },
    // Diálogo "Ignorar y cerrar": qué queda sin importar.
    ignoreSubtitle() {
      const left = this.rows.filter((row) => !row.saved).length;
      return `${plural(left, "producto no se importará", "productos no se importarán")}. Se cierra esta importación y se vuelve a la pantalla inicial.`;
    },
    ignoreSummary() {
      const saved = this.rows.filter((row) => row.saved).length;
      return [{ icon: "mdi-check-circle-outline", text: `${plural(saved, "producto ya importado", "productos ya importados")}: no se deshace` }];
    },
    ignoreRows() {
      if (!this.ignoreDialog) return [];
      return this.rows
        .filter((row) => !row.saved)
        .slice(0, PREVIEW_ROWS)
        .map((row) => ({ excelRow: row.excelRow, name: row.name, detail: row.status === "Lista" ? "Lista, pero no se importará" : this.rowDetail(row) }));
    },
    ignoreTotal() {
      return this.rows.filter((row) => !row.saved).length;
    },
    // Todo lo cargado ya se importó: en vez de Guardar se ofrece volver a la pantalla inicial.
    importFinished() {
      return this.rows.length > 0 && this.rows.every((row) => row.saved);
    },
    // Guardar también se puede presionar sin filas listas si hay segmentaciones por crear: el diálogo las ofrece primero.
    saveDisabled() {
      return !!this.saveBlockReason && !(this.newSegmentationCount && !this.selectedValidRows.length && !this.loading);
    },
    segmentationSubtitle() {
      return this.segmentationThenImport
        ? "Estos productos usan familias, categorías o subcategorías que aún no existen. Créelas antes de importar."
        : "Se agregan al catálogo. Después podrá importar las filas que las usan.";
    },
    segmentationConfirmLabel() {
      return this.segmentationThenImport ? "Crear y continuar" : "Crear segmentaciones";
    },
    // Solo se puede seguir sin crearlas si hay algo más que importar.
    segmentationSecondaryLabel() {
      return this.segmentationThenImport && this.selectedValidRows.length ? "Continuar sin crearlas" : "";
    },
    warehouseName() {
      const warehouse = this.warehouses.find((item) => item.ID_Warehouse === this.warehouse);
      return warehouse ? warehouse.Warehouse : "";
    },
    // Diálogo de confirmación al guardar. Las listas solo se calculan con el diálogo abierto (hasta 1000 filas).
    importTitle() {
      return `Importar ${plural(this.selectedValidRows.length, "producto", "productos")}`;
    },
    importSummary() {
      return this.needsWarehouse ? [{ icon: "mdi-warehouse", text: `Existencia inicial en ${this.warehouseName}` }] : [];
    },
    importRows() {
      if (!this.importDialog) return [];
      return this.selectedValidRows.slice(0, PREVIEW_ROWS).map((row) => ({
        excelRow: row.excelRow,
        name: row.name,
        detail: [
          [this.nameById(this.families, row.family), this.nameById(this.categories, row.category), this.nameById(this.subcategories, row.subcategory)].filter(Boolean).join(" › "),
          row.idProvider ? `Proveedor: ${this.providerLabel(row.idProvider)}` : "",
        ].filter(Boolean).join(" · "),
        measureUnit: row.measureUnit,
        price: formatAmount(row.price),
        stock: formatAmount(row.stock),
      }));
    },
    // Filas pendientes que este guardado no incluye, con el motivo.
    importNotice() {
      if (!this.importDialog) return "";
      const included = new Set(this.selectedValidRows.map((row) => row._rowId));
      const left = this.rows.filter((row) => !row.saved && !included.has(row._rowId));
      if (!left.length) return "";
      const reasons = [
        [left.filter((row) => row.status === "Con errores").length, "con errores"],
        [left.filter((row) => row.status === "Nueva").length, "por crear segmentaciones"],
        [left.filter((row) => row.status === "Lista").length, "sin seleccionar"],
      ].filter(([count]) => count).map(([count, reason]) => `${formatCount(count)} ${reason}`);
      return `${plural(left.length, "fila no se importará", "filas no se importarán")} ahora: ${reasons.join(" · ")}.`;
    },
    // Diálogo de segmentaciones nuevas: qué producto trae cada una.
    segmentationSummary() {
      const { familiesToCreate, catsToCreate, subcatsToCreate } = this.segmentationToCreate;
      return [[familiesToCreate.length, "familia", "familias"], [catsToCreate.length, "categoría", "categorías"], [subcatsToCreate.length, "subcategoría", "subcategorías"]]
        .filter(([count]) => count)
        .map(([count, one, many]) => ({ icon: "mdi-shape-outline", text: plural(count, one, many) }));
    },
    // Mismo criterio que segmentationToCreate: no se listan nuevas sin padre (no se crean; la fila sigue con error).
    segmentationRows() {
      return this.segmentationAll.slice(0, PREVIEW_ROWS);
    },
    segmentationTotal() {
      return this.segmentationAll.length;
    },
    segmentationAll() {
      if (!this.segmentationDialog) return [];
      return this.rows.filter((row) => !row.saved).map((row) => {
        const creates = this.creatableSegments(row);
        return {
          excelRow: row.excelRow,
          name: row.name,
          news: SEGMENTS.filter(({ field }) => creates[field]).map(({ field }) => ({ label: FIELD_LABELS[field], name: row.raw[field] })),
        };
      }).filter((row) => row.news.length);
    },
    saveBlockReason() {
      if (this.loading) return "Procesando...";
      if (!this.selectedValidRows.length) return this.rows.length ? "No hay filas seleccionadas y válidas." : "Cargue un archivo.";
      if (this.selectedValidRows.length > MAX_ROWS) return `Máximo ${formatCount(MAX_ROWS)} productos por importación.`;
      if (this.warehouseMissing) return WAREHOUSE_REQUIRED;
      return "";
    },
    // Payload igual a V1 (ManageProductImport.vue:2070-2132) para items/importDataTypes.
    // El backend une hijo-padre nuevos por nombre exacto (==): cada nombre se manda con una sola escritura (la primera vista).
    // Limitación conocida: dos categorías nuevas con el mismo nombre en familias nuevas distintas se confunden en el backend (igual que V1).
    segmentationToCreate() {
      const familiesToCreate = [];
      const catsToCreate = [];
      const subcatsToCreate = [];
      const names = {};
      const canonical = (scope, text) => {
        const key = `${scope}|${normalize(text)}`;
        if (!names[key]) names[key] = text;
        return names[key];
      };
      const seen = new Set();
      const once = (key, push) => { if (!seen.has(key)) { seen.add(key); push(); } };

      this.rows.filter((row) => !row.saved).forEach((row) => {
        const creates = this.creatableSegments(row);
        const family = creates.family ? canonical("f", row.raw.family) : null;
        const category = creates.category ? canonical(`c|${row.family || normalize(family)}`, row.raw.category) : null;

        if (family) once(`f|${normalize(family)}`, () => familiesToCreate.push({ name: family }));
        if (category && row.family) once(`c|${row.family}|${normalize(category)}`, () => catsToCreate.push({ name: category, id_family: row.family }));
        if (category && family) once(`c|${normalize(family)}|${normalize(category)}`, () => catsToCreate.push({ name: category, id_family: null, name_family: family }));
        if (creates.subcategory) {
          const subcategory = canonical(`s|${row.category || normalize(category)}`, row.raw.subcategory);
          if (row.category) once(`s|${row.category}|${normalize(subcategory)}`, () => subcatsToCreate.push({ name: subcategory, id_cat: row.category }));
          // Su categoría nueva también va en el payload (creatableSegments); si no, el backend no encuentra el padre y revierte todo.
          else once(`s|${normalize(category)}|${normalize(subcategory)}`, () => subcatsToCreate.push({ name: subcategory, id_cat: null, name_cat: category }));
        }
      });
      return { familiesToCreate, catsToCreate, subcatsToCreate };
    },
    newSegmentationCount() {
      const { familiesToCreate, catsToCreate, subcatsToCreate } = this.segmentationToCreate;
      return familiesToCreate.length + catsToCreate.length + subcatsToCreate.length;
    },
  },
  watch: {
    // Al volverse obligatorio el almacén (cargar archivo, marcar filas, editar existencia) el campo se sacude una vez.
    warehouseMissing(missing) {
      if (missing) this.nudgeWarehouse();
    },
  },
  created() {
    // Funciones estables (no reactivas): una flecha en el template se recrea en cada render y reinicia las columnas del grid.
    this.display = {
      measureUnit: (row) => this.displayLookup(row, "measureUnit", row.measureUnit),
      family: (row) => this.displayLookup(row, "family", this.nameById(this.families, row.family)),
      category: (row) => this.displayLookup(row, "category", this.nameById(this.categories, row.category)),
      subcategory: (row) => this.displayLookup(row, "subcategory", this.nameById(this.subcategories, row.subcategory)),
      idProvider: (row) => this.displayLookup(row, "idProvider", this.providerLabel(row.idProvider)),
    };
    // JSON de Lottie sin reactividad: Vue no necesita observar miles de nodos.
    this.cloudAnimation = cloudAnimation;
    this.loaderAnimation = loaderAnimation;
    this.icons = { sheet: sheetIcon, check: checkIcon, alert: alertIcon };
    this.touchedFields = null;
    // Campos y bloques del formulario de corrección (sin reactividad): la etiqueta es la de la tabla, con * en los obligatorios.
    this.rowFields = Object.entries(FIELD_LABELS).map(([key, label]) => ({ key, caption: REQUIRED_HEADERS.includes(key) ? `${label}*` : label }));
    this.rowGroups = ROW_GROUPS;
    // Último guardado enviado { key, fingerprint }; no reactivo. Decide si el próximo guardado es un reintento (ver saveImport).
    // Limitación conocida: vive solo en este componente; recargar la página o salir de la pantalla pierde la clave (mejora posible: sessionStorage).
    this.importAttempt = null;
  },
  mounted() {
    this.loadCatalogs();
    // Cerrar o recargar el navegador con filas sin importar: el navegador pide confirmación.
    this.onBeforeUnload = (event) => {
      if (!this.hasUnsavedWork) return;
      event.preventDefault();
      event.returnValue = "";
    };
    if (typeof window !== "undefined") window.addEventListener("beforeunload", this.onBeforeUnload);
  },
  beforeDestroy() {
    if (typeof window !== "undefined") window.removeEventListener("beforeunload", this.onBeforeUnload);
  },
  methods: {
    async loadCatalogs() {
      this.loading = true;
      this.busyText = "Cargando catálogos…";
      try {
        const [families, categories, subcategories, warehouses, providers] = await Promise.all([
          this.$API.itemtypes.getFamilies(),
          this.$API.itemtypes.getCategories(),
          this.$API.itemtypes.getSubs(),
          this.$API.warehouse.getWarehouses({ notShowReserve: 1 }),
          this.$API.providers.getProviders(),
          this.$store.dispatch("getCatalogMH"),
        ]);
        this.families = families || [];
        this.categories = categories || [];
        this.subcategories = subcategories || [];
        this.warehouses = warehouses || [];
        // Solo proveedores de inventario (TypeProvider 0); provider-list también trae los de gastos (1).
        this.providers = (providers || []).filter((provider) => provider.TypeProvider === 0);
        this.units = this.$store.getters.getCatalogMH("UnidadMedida") || [];
      } catch (error) {
        this.showAlert("danger", "Error", `No se pudieron cargar los catálogos: ${this.errorMessage(error)}`);
      } finally {
        this.loading = false;
      }
    },

    // ---------- Plantilla ----------
    async downloadTemplate() {
      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet("Productos");
      const catalogs = workbook.addWorksheet("Catalogos");
      const instructions = workbook.addWorksheet("Instrucciones");

      // Si el negocio exige proveedor, la plantilla lo dice (el lector ignora "*" y "(opcional)").
      sheet.addRow(COLUMNS.map((column) => (column.key === "provider" && this.providerRequired ? "Proveedor*" : column.header)));
      sheet.columns = COLUMNS.map((column) => ({ width: column.width }));
      sheet.getRow(1).font = { bold: true };
      sheet.getColumn(8).numFmt = "@"; // código de barra como texto (Excel corta números largos)

      const lists = [
        { title: "Unidades de medida", values: this.units.map((unit) => unit.Valores), column: 4 },
        { title: "Familias", values: this.families.map((item) => item.name), column: 10 },
        { title: "Categorías", values: this.categories.map((item) => item.name), column: 11 },
        { title: "Subcategorías", values: this.subcategories.map((item) => item.name), column: 12 },
        { title: "Proveedores", values: this.providers.map((item) => item.ProviderName || item.ProviderLegalName), column: 13 },
      ];
      lists.forEach((list, index) => {
        const letter = String.fromCharCode(65 + index);
        catalogs.getCell(`${letter}1`).value = list.title;
        list.values.forEach((value, row) => { catalogs.getCell(`${letter}${row + 2}`).value = value; });
        catalogs.getColumn(index + 1).width = 28;
        if (!list.values.length) return;
        for (let row = 2; row <= TEMPLATE_VALIDATION_ROWS + 1; row++) {
          sheet.getCell(row, list.column).dataValidation = {
            type: "list",
            allowBlank: true,
            formulae: [`'Catalogos'!$${letter}$2:$${letter}$${list.values.length + 1}`],
            showErrorMessage: false,
          };
        }
      });
      catalogs.getRow(1).font = { bold: true };
      for (let row = 2; row <= TEMPLATE_VALIDATION_ROWS + 1; row++) {
        sheet.getCell(row, 9).dataValidation = { type: "list", allowBlank: true, formulae: ['"Medicamento,Vacuna"'] };
      }

      [
        ["Instrucciones"],
        ["Llene la hoja Productos desde la fila 2. Las columnas con * son obligatorias. Máximo " + formatCount(MAX_ROWS) + " productos por importación. Las listas desplegables de las columnas están en las primeras " + formatCount(TEMPLATE_VALIDATION_ROWS) + " filas; en las demás escriba el valor igual que en la hoja Catalogos."],
        ["Precio, costo y existencia: solo números, sin símbolo $ (ej. 1250.50)."],
        ["Unidad, familia, categoría, subcategoría y proveedor: use los nombres de la hoja Catalogos. Si algo no coincide lo corrige en pantalla."],
        ["Familias, categorías o subcategorías nuevas se pueden crear en pantalla antes de guardar."],
        ["El nombre no puede repetirse en el archivo ni ser igual al de un producto ya creado; si pasa, en pantalla le cambia el nombre o quita la fila."],
        [this.providerRuleText + " El proveedor se busca por NIT o por nombre."],
        ["Si carga existencia, seleccione el almacén en pantalla antes de guardar."],
        [],
        ["Ejemplo (no lo copie a la hoja Productos):"],
        COLUMNS.map((column) => column.header),
        ["Café molido 500 g", "Café molido", "Bolsa de 500 g", this.units[0] ? this.units[0].Valores : "Unidad", 4.5, 3, 10, "7411000000001", "", "Bebidas", "Café", "Molido", "", ""],
      ].forEach((row) => instructions.addRow(row));
      instructions.getColumn(1).width = 120;
      instructions.getRow(1).font = { bold: true };

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer], { type: "application/octet-stream" }), "Plantilla_Importacion_Productos_V2.xlsx");
    },

    // ---------- Carga ----------
    openPicker() {
      if (!this.loading) this.$refs.excelInput.click();
    },
    // Reinicia la animación aunque ya estuviera sacudiéndose (primero se quita la clase, luego se vuelve a poner).
    nudgeWarehouse() {
      if (!this.warehouseMissing) return;
      this.shaking = false;
      this.$nextTick(() => { this.shaking = true; });
    },
    // accept= del input no aplica al soltar: el tipo y la cantidad se validan aquí.
    onDrop(event) {
      this.dragging = false;
      if (this.loading) return;
      const files = event.dataTransfer.files;
      if (files.length !== 1) return this.showAlert("warning", "Archivo no válido", "Suelte un solo archivo Excel.");
      if (!/\.xlsx?$/i.test(files[0].name)) return this.showAlert("warning", "Archivo no válido", "Solo se permiten archivos .xlsx o .xls.");
      this.readFile(files[0]);
    },
    handleFileChange(event) {
      const file = event.target.files[0];
      event.target.value = "";
      return file ? this.readFile(file) : undefined;
    },
    readFile(file) {
      this.loading = true;
      this.busyText = file.size > 2 * 1024 * 1024 ? "Leyendo archivo grande… puede tardar unos segundos" : "Leyendo archivo…";
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onerror = () => {
          this.loading = false;
          this.showAlert("danger", "Error", "No se pudo leer el archivo.");
          resolve();
        };
        reader.onload = async (readerEvent) => {
          // Leer el Excel bloquea la pantalla: primero se deja pintar el indicador.
          await this.$nextTick();
          await new Promise((frame) => requestAnimationFrame(() => setTimeout(frame, 0)));
          try {
            const workbook = XLSX.read(new Uint8Array(readerEvent.target.result), { type: "array" });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = this.parseRows(XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "", blankrows: true }));
            if (!rows.length) {
              this.showAlert("warning", "Sin productos", "No se encontraron filas con datos en la hoja Productos.");
              return;
            }
            rows.forEach((row) => this.resolveRow(row));
            this.rows = rows;
            this.selectedRowKeys = rows.map((row) => row._rowId);
            this.result = null;
            this.validateAll();
            this.fileHash = hashRows(rows);
            this.previousImport = this.findPreviousImport(this.fileHash);
            this.bulkChoice = {};
            const { ready, attention } = this.rowCounts;
            this.reviewEnabled = rows.length >= REVIEW_MIN_ROWS && attention > 0;
            this.view = this.reviewEnabled ? "attention" : "all";
            if (this.reviewEnabled) this.captureReview();
            this.showAlert("success", "Archivo cargado", this.reviewEnabled
              ? `${rows.length} filas cargadas: ${ready} listas y ${attention} necesitan atención.`
              : `${rows.length} filas listas para revisar.`);
          } catch (error) {
            this.showAlert("danger", "Error", `No se pudo leer el archivo: ${this.errorMessage(error)}`);
          } finally {
            this.loading = false;
            resolve();
          }
        };
        reader.readAsArrayBuffer(file);
      });
    },
    parseRows(sheetRows) {
      const headerIndex = sheetRows.findIndex((row) => {
        const headers = row.map(normalizeHeader);
        return REQUIRED_HEADERS.every((key) => headers.includes(normalizeHeader(COLUMNS.find((c) => c.key === key).header)));
      });
      if (headerIndex < 0) throw new Error("El archivo no tiene los encabezados de la plantilla V2 (Nombre, Unidad de medida, Precio de venta).");

      const position = {};
      sheetRows[headerIndex].forEach((value, index) => {
        const column = COLUMNS.find((c) => normalizeHeader(c.header) === normalizeHeader(value));
        if (column && position[column.key] === undefined) position[column.key] = index;
      });

      return sheetRows.slice(headerIndex + 1).reduce((rows, cells, offset) => {
        const cell = (key) => (position[key] === undefined ? "" : cells[position[key]]);
        if (COLUMNS.every((column) => cleanText(cell(column.key)) === "")) return rows;
        const amount = (key) => this.parseAmount(cell(key));
        // Como la importación anterior: con uno solo de los dos nombres basta (si falta "Nombre", se usa el comercial).
        // Si falta el comercial, el servidor usa el nombre.
        const comercialName = cleanText(cell("comercialName"));
        rows.push({
          _rowId: rows.length + 1,
          excelRow: headerIndex + offset + 2,
          name: cleanText(cell("name")) || comercialName,
          comercialName,
          description: cleanText(cell("description")),
          price: amount("price"),
          cost: amount("cost"),
          stock: amount("stock"),
          barcode: cleanText(cell("barcode")),
          measureUnit: null,
          aditionalType: "",
          family: null,
          category: null,
          subcategory: null,
          idProvider: null,
          raw: {
            measureUnit: cleanText(cell("measureUnit")),
            aditionalType: cleanText(cell("aditionalType")),
            family: cleanText(cell("family")),
            category: cleanText(cell("category")),
            subcategory: cleanText(cell("subcategory")),
            provider: cleanText(cell("provider")),
            nit: cleanText(cell("nit")),
            price: cleanText(cell("price")),
            cost: cleanText(cell("cost")),
            stock: cleanText(cell("stock")),
          },
          ambiguous: {},
          errors: {},
          serverErrors: {},
          duplicateOf: null, // fila del Excel con el mismo nombre que aparece antes (esta es la repetida)
          status: "Lista",
          saved: false,
          inReview: false, // entró al paso de revisión: se queda en la lista aunque se corrija
          ID_Item: null,
        });
        return rows;
      }, []);
    },
    // Nada de adivinar comas: "1250.5" o "1,250.50"; otra forma de texto es inválida (null + error).
    parseAmount(value) {
      if (typeof value === "number") return cleanExcelNumber(value);
      const text = cleanText(value).replace(/[$\s]/g, "");
      if (!text) return null;
      if (/^\d+(\.\d+)?$/.test(text) || /^\d{1,3}(,\d{3})+(\.\d+)?$/.test(text)) return Number(text.replace(/,/g, ""));
      return null;
    },

    // ---------- Resolución nombre -> ID (sin mayúsculas ni tildes) ----------
    resolveRow(row) {
      const unit = this.units.find((item) => normalize(item.Valores) === normalize(row.raw.measureUnit));
      row.measureUnit = unit ? unit.Valores : null;

      const aditional = this.aditionalTypes.find((item) => normalize(item.value) === normalize(row.raw.aditionalType));
      row.aditionalType = aditional ? aditional.value : null; // null: elegir "(ninguno)" sí es un cambio y limpia el error

      this.resolveSegmentation(row);
      this.resolveProvider(row);
    },
    resolveSegmentation(row) {
      SEGMENTS.forEach(({ field, list, parentField, parentKey }) => {
        row.ambiguous[field] = false;
        if (!row.raw[field] || row[field]) return;
        let matches = this[list].filter((item) => normalize(item.name) === normalize(row.raw[field]));
        if (parentField && row[parentField]) matches = matches.filter((item) => item[parentKey] === row[parentField]);
        else if (parentField && row.raw[parentField]) matches = []; // el padre es nuevo: el hijo también
        if (matches.length === 1) row[field] = matches[0].id;
        row.ambiguous[field] = matches.length > 1;
      });
      // Padre vacío en el Excel: si el hijo es único, se infiere (Norbin: permitir completar la jerarquía en pantalla).
      // Si el Excel sí trae un padre (aunque sea nuevo), no se pisa.
      if (row.subcategory && !row.category && !row.raw.category) row.category = this.categoryOfSubcategory(row.subcategory);
      if (row.category && !row.family && !row.raw.family) row.family = this.familyOfCategory(row.category);
    },
    // Padre de una subcategoría o categoría solo si también está en el catálogo: hay huérfanas en datos reales
    // (antes tumbaban la carga del archivo o dejaban "Lista" una familia inexistente). Sin padre, validateRow pide elegirlo.
    categoryOfSubcategory(id) {
      const subcategory = this.subcategories.find((item) => item.id === id);
      return subcategory && this.categories.some((item) => item.id === subcategory.id_cat) ? subcategory.id_cat : null;
    },
    familyOfCategory(id) {
      const category = this.categories.find((item) => item.id === id);
      return category && this.families.some((item) => item.id === category.id_family) ? category.id_family : null;
    },
    resolveProvider(row) {
      row.ambiguous.idProvider = false;
      if (!row.raw.provider && !row.raw.nit) return;
      const nit = normalizeNit(row.raw.nit); // "-" o "N/A" quedan vacíos: no deben coincidir con proveedores sin NIT
      let matches = nit ? this.providers.filter((item) => normalizeNit(item.ProviderLegalNIT) === nit) : [];
      if (!matches.length && row.raw.provider) {
        matches = this.providers.filter((item) => [item.ProviderName, item.ProviderLegalName].some((name) => normalize(name) === normalize(row.raw.provider)));
      }
      if (matches.length === 1) row.idProvider = matches[0].ID_Provider;
      row.ambiguous.idProvider = matches.length > 1;
    },

    // ---------- Validación (misma regla que el DTO del backend) ----------
    validateAll() {
      const barcodeCount = this.barcodeCounts(this.rows);
      const firstByName = this.firstByName(this.rows);
      this.rows.forEach((row) => this.validateRow(row, barcodeCount, firstByName));
    },
    // Nombre repetido en el archivo: la primera fila con ese nombre queda; las siguientes se marcan para cambiarles el nombre o quitarlas.
    firstByName(rows) {
      const first = new Map();
      [...rows.filter((row) => row.saved), ...rows.filter((row) => !row.saved)].forEach((row) => {
        const key = normalize(row.name);
        if (key && !first.has(key)) first.set(key, row);
      });
      return first;
    },
    barcodeCounts(rows) {
      const barcodeCount = {};
      rows.forEach((row) => {
        const key = normalize(row.barcode);
        if (key) barcodeCount[key] = (barcodeCount[key] || 0) + 1;
      });
      return barcodeCount;
    },
    validateRow(row, barcodeCount, firstByName) {
      const errors = {};
      const text = (field, max, required, freeText) => {
        const value = row[field] || "";
        if (!value && required) errors[field] = "Campo obligatorio";
        else if (value.length > max) errors[field] = `Máximo ${max} caracteres`;
        else if (freeText && FORMULA_START.test(value)) errors[field] = "No puede iniciar con =, +, - o @";
      };
      const amount = (field, required) => {
        const value = row[field];
        if (value === null || value === undefined || value === "") {
          if (row.raw[field]) errors[field] = `Número inválido: "${row.raw[field]}"`;
          else if (required) errors[field] = "Campo obligatorio";
        } else if (Number(value) < 0) errors[field] = "Debe ser mayor o igual a 0";
        else if (Number(value) > MAX_AMOUNT) errors[field] = `Debe ser menor o igual a ${MAX_AMOUNT}`;
        else if (Math.round(Number(value) * 100) / 100 !== Number(value)) errors[field] = "Máximo 2 decimales";
      };
      const lookup = (field, label) => {
        if (row[field] || !row.raw[field]) return;
        errors[field] = row.ambiguous[field]
          ? `Hay varias coincidencias para "${row.raw[field]}"; seleccione una`
          : `No existe ${label} "${row.raw[field]}"; seleccione una`;
      };

      text("name", 150, true, true);
      text("comercialName", 150, false, true);
      text("description", 250, false, true);
      text("barcode", 50, false, true);
      amount("price", true);
      amount("cost", false);
      amount("stock", false);

      if (!row.measureUnit) {
        if (row.raw.measureUnit) lookup("measureUnit", "la unidad");
        else errors.measureUnit = "Campo obligatorio";
      }
      if (row.raw.aditionalType && !row.aditionalType) errors.aditionalType = `Valor no permitido: "${row.raw.aditionalType}"`;
      if (row.barcode && barcodeCount[normalize(row.barcode)] > 1) errors.barcode = "Código de barra repetido en el archivo";
      const first = row.name && firstByName.get(normalize(row.name));
      row.duplicateOf = first && first._rowId !== row._rowId ? first.excelRow : null;
      if (row.duplicateOf && !errors.name) errors.name = `Nombre repetido en el archivo (igual a la fila ${row.duplicateOf}): cámbielo o quite la fila`;

      // Jerarquía (Norbin 00:41): se valida, pero se deja elegir el padre en pantalla.
      SEGMENTS.forEach(({ field }) => { if (row.ambiguous[field]) lookup(field, ""); });
      const newCategory = row.raw.category && !row.category && !row.ambiguous.category;
      const newSubcategory = row.raw.subcategory && !row.subcategory && !row.ambiguous.subcategory;
      if ((row.category || newCategory) && !row.family && !row.raw.family) errors.family = "Seleccione la familia de esta categoría";
      if ((row.subcategory || newSubcategory) && !row.category && !row.raw.category) errors.category = "Seleccione la categoría de esta subcategoría";
      const category = row.category && this.categories.find((item) => item.id === row.category);
      if (category && row.family && category.id_family !== row.family) errors.category = "La categoría no pertenece a la familia";
      const subcategory = row.subcategory && this.subcategories.find((item) => item.id === row.subcategory);
      if (subcategory && row.category && subcategory.id_cat !== row.category) errors.subcategory = "La subcategoría no pertenece a la categoría";

      if (row.ambiguous.idProvider || (!row.idProvider && (row.raw.provider || row.raw.nit))) {
        errors.idProvider = row.ambiguous.idProvider
          ? "Hay varios proveedores con ese nombre; seleccione uno"
          : `No existe el proveedor de inventario "${row.raw.provider || row.raw.nit}"; seleccione uno${this.providerRequired ? "" : " o déjelo vacío"}`;
      } else if (!row.idProvider && this.providerRequired) {
        errors.idProvider = "Proveedor obligatorio: el negocio relaciona productos con proveedores";
      }

      row.errors = { ...errors, ...row.serverErrors };
      const isNewSegment = SEGMENTS.some(({ field }) => row.raw[field] && !row[field] && !row.ambiguous[field]);
      if (row.saved) row.status = "Importada";
      else if (Object.keys(row.errors).length) row.status = "Con errores";
      else if (isNewSegment) row.status = "Nueva";
      else row.status = "Lista";
    },

    // ---------- Grid ----------
    isRowEditable(e) {
      return !e.row.data.saved;
    },
    onSelectionChanged(e) {
      this.selectedRowKeys = e.selectedRowKeys;
    },
    onEditorPreparing(e) {
      if (e.parentType !== "dataRow") return;
      if (e.command === "select") {
        if (e.row && e.row.data.status !== "Lista") e.editorOptions.disabled = true;
        return;
      }
      const data = e.row && e.row.data;
      if (!data) return;
      // Sin el texto como 2º argumento, DevExtreme no cachea displayValueMap[valor]; con caché, limpiar una celda
      // borraba el texto del Excel de todas las filas con ese mismo valor vacío.
      if (e.lookup) {
        e.editorOptions.onValueChanged = (args) => {
          e.setValue(args.value);
          // En modo celda lo elegido queda pendiente hasta salir de la celda: si lo eligió el usuario se cierra la edición para que se guarde al instante.
          if (args.event) setTimeout(() => e.component.closeEditCell(), 0);
        };
      }
      if (e.dataField === "category" && data.family) e.editorOptions.dataSource = this.categoriesOf(data.family);
      if (e.dataField === "subcategory" && data.category) e.editorOptions.dataSource = this.subcategoriesOf(data.category);
    },
    // Solo campos planos en newData: DevExtreme hace merge profundo (deepExtendArraySafe) y no borraría llaves de objetos anidados.
    onRowUpdating(e) {
      // Solo lo que el usuario editó; la cascada no borra el texto del Excel de los hijos.
      this.touchedFields = { key: e.key, fields: Object.keys(e.newData) };
      this.cascadeSegments(e.oldData, e.newData);
    },
    onRowUpdated(e) {
      const row = this.rows.find((item) => item._rowId === e.key);
      const fields = this.touchedFields && this.touchedFields.key === e.key ? this.touchedFields.fields : [];
      if (row) this.settleRow(row, fields);
      this.touchedFields = null;
      this.validateAll();
      this.repaintGrid();
    },
    // Una sola lógica para la tabla y el formulario. La tabla la usa en dos tiempos, porque DevExtreme escribe el valor entre onRowUpdating
    // (cascadeSegments) y onRowUpdated (settleRow); el formulario, completa y sobre cualquier fila o copia (applyRowChanges).
    // Cascada como JsonProductManager, pero solo se limpian hijos con ID; un hijo "Nuevo" conserva su nombre y se vuelve a resolver bajo el padre elegido (settleRow).
    // Un hijo que viene en el mismo cambio (formulario) se respeta. Escribe en newData: solo campos planos.
    cascadeSegments(oldData, newData) {
      const value = (field) => (field in newData ? newData[field] : oldData[field]);
      if ("family" in newData && newData.family !== oldData.family) {
        if (oldData.category && !("category" in newData)) newData.category = null;
        if (oldData.subcategory && !("subcategory" in newData)) newData.subcategory = null;
      }
      if ("category" in newData && newData.category !== oldData.category && oldData.subcategory && !("subcategory" in newData)) newData.subcategory = null;
      // Padre vacío: se completa desde el hijo elegido.
      if (newData.subcategory && !value("category")) newData.category = this.categoryOfSubcategory(newData.subcategory);
      if (value("category") && !value("family")) newData.family = this.familyOfCategory(value("category"));
    },
    // Lo editado deja de depender del texto del Excel y de los errores del servidor.
    settleRow(row, fields) {
      const raw = { ...row.raw };
      const serverErrors = { ...row.serverErrors };
      const ambiguous = { ...row.ambiguous };
      fields.forEach((field) => {
        delete serverErrors[field];
        ambiguous[field] = false;
        if (field === "idProvider") { raw.provider = ""; raw.nit = ""; } else if (field in raw) raw[field] = "";
      });
      row.raw = raw;
      row.serverErrors = serverErrors;
      row.ambiguous = ambiguous;
      this.resolveSegmentation(row);
    },
    // Escribe cambios en una fila como al editar la celda: cascada, el valor y soltarla del Excel y del servidor. No revalida: eso lo hace quien llama.
    applyRowChanges(row, changes) {
      const data = { ...changes };
      this.cascadeSegments(row, data);
      Object.assign(row, data);
      this.settleRow(row, Object.keys(changes));
    },
    onCellPrepared(e) {
      if (e.rowType !== "data" || !e.column.dataField || !e.data.errors) return;
      const field = e.column.dataField;
      if (e.data.errors[field]) {
        e.cellElement.classList.add("cell-error");
        e.cellElement.title = e.data.errors[field];
      } else if (["family", "category", "subcategory"].includes(field) && e.data.raw[field] && !e.data[field] && !e.data.saved) {
        e.cellElement.classList.add("cell-new");
        e.cellElement.title = `Nueva: "${e.data.raw[field]}" se crea con el botón "Crear segmentaciones nuevas"`;
      }
    },
    displayLookup(row, field, resolvedText) {
      if (row[field]) return resolvedText;
      if (row[field] === "") return field === "idProvider" ? "(sin proveedor)" : "(ninguna)"; // elegido a propósito
      if (field === "idProvider") return row.raw.provider || row.raw.nit || "";
      return row.raw[field] || "";
    },
    withNone(list) {
      return [{ id: "", name: "(ninguna)" }, ...list];
    },
    // Categorías de la familia y subcategorías de la categoría, con la opción vacía: las que ofrecen la tabla y el formulario.
    categoriesOf(family) {
      return this.withNone(this.categories.filter((item) => item.id_family === family));
    },
    subcategoriesOf(category) {
      return this.withNone(this.subcategories.filter((item) => item.id_cat === category));
    },
    nameById(list, id) {
      const item = list.find((entry) => entry.id === id);
      return item ? item.name : "";
    },
    providerLabel(id) {
      const option = this.providerOptions.find((item) => item.ID_Provider === id);
      return option ? option.label : "";
    },
    // En el paso de revisión la tabla está filtrada: hay que volver a consultar para que las filas ya corregidas salgan.
    // Tras escribir filas sin pasar por la tabla (formulario) también: repaint() redibuja con los valores en caché.
    repaintGrid(reload) {
      const grid = this.$refs.grid && this.$refs.grid.instance;
      if (!grid) return;
      if (reload || this.gridFilter) grid.refresh();
      else grid.repaint();
    },
    // Lo que ve el usuario en la columna Estado: además del estado de la fila cuenta si está marcada para importar.
    rowState(row) {
      const state = { Importada: STATES.imported, "Con errores": STATES.errors, Nueva: STATES.new }[row.status];
      if (state) return state;
      return this.selectedRowKeys.includes(row._rowId) ? STATES.ready : STATES.unselected;
    },
    rowErrorText(row, separator = "\n") {
      return Object.entries(row.errors || {}).map(([field, message]) => `${FIELD_LABELS[field] || field}: ${message}`).join(separator);
    },
    // Texto visible (no solo tooltip): qué falla y cómo corregirlo, qué se creará o con qué ID quedó guardada.
    rowDetail(row) {
      if (row.saved) return `Creado como ${row.ID_Item}`;
      if (Object.keys(row.errors || {}).length) return this.rowErrorText(row, " · ");
      const pending = SEGMENTS.filter(({ field }) => row.raw[field] && !row[field] && !row.ambiguous[field])
        .map(({ field }) => `${FIELD_LABELS[field]} "${row.raw[field]}"`);
      if (pending.length) return `Por crear con "Crear segmentaciones nuevas": ${pending.join(", ")}`;
      if (row.inReview && row.status === "Lista") return "Corregida: lista para importar.";
      return this.rowState(row) === STATES.unselected ? "Sin seleccionar: no se importará." : "";
    },

    // ---------- Formulario de corrección de una fila ----------
    // Chip de Estado (filas con errores o "Nueva") y texto de Detalle (filas con texto) abren el formulario de corrección; las filas guardadas no se editan.
    fixer(row, cell) {
      if (row.saved || !(cell === "status" ? ["Con errores", "Nueva"].includes(row.status) : this.rowDetail(row))) return { attrs: {}, on: {} };
      const open = (event) => { event.preventDefault(); if (!this.loading) { this.editingRow = row; this.rowDialog = true; } }; // ocupado (leyendo o guardando): la tabla está bloqueada
      return { attrs: { class: "row-opener", role: "button", tabindex: "0", "aria-label": `Corregir la fila ${row.excelRow}` }, on: { click: open, keydown: (event) => { if (event.key === "Enter" || event.key === " ") open(event); } } };
    },
    // Con qué arranca cada campo del formulario: el valor de la fila o, si no se resolvió, el texto del Excel (se ve junto a su error). Los montos, como texto.
    formValue(row, key) {
      if (AMOUNTS.includes(key)) return row[key] === null ? row.raw[key] : String(row[key]);
      return row[key] ?? ((key === "idProvider" ? row.raw.provider || row.raw.nit : row.raw[key]) || "");
    },
    // Opciones de cada campo según el borrador: categoría y subcategoría, las del padre elegido (como en la tabla; sin padre elegido, todas); null: texto libre.
    // Limitación conocida: al cambiar el padre el borrador no vacía los hijos: el actual se sigue viendo con su nombre, y la vista previa y Aplicar sí lo limpian (cascada de la tabla).
    itemsForField({ key }, draft) {
      const options = (list, all, current) => {
        const known = all.find((item) => item.id === current);
        return (known && !list.includes(known) ? [known, ...list] : list).map(({ id, name }) => ({ text: name, value: id }));
      };
      switch (key) {
        case "measureUnit": return this.units.map((unit) => unit.Valores);
        case "aditionalType": return this.aditionalTypes;
        case "family": return options(this.familyOptions, this.families, draft.family);
        case "category": return options(typeof draft.family === "number" ? this.categoriesOf(draft.family) : this.categoryOptions, this.categories, draft.category);
        case "subcategory": return options(typeof draft.category === "number" ? this.subcategoriesOf(draft.category) : this.subcategoryOptions, this.subcategories, draft.subcategory);
        case "idProvider": return this.providerItems;
        default: return null;
      }
    },
    // Vista previa del formulario: la fila con lo escrito, evaluada como la evalúa la tabla, sobre una copia (las filas reales no cambian hasta Aplicar).
    // Solo se evalúa la fila editada: lo único que toma de las demás son los códigos de barra y nombres repetidos, que ya incluyen la copia.
    validateDraft(draft, changes) {
      const row = this.editingRow;
      if (!Object.keys(changes).length) return { errors: { ...row.errors }, status: row.status, detail: this.rowDetail(row) };
      const copy = { ...row, raw: { ...row.raw }, ambiguous: { ...row.ambiguous }, errors: { ...row.errors }, serverErrors: { ...row.serverErrors } };
      this.applyFormChanges(copy, changes);
      const rows = this.rows.map((item) => (item === row ? copy : item));
      this.validateRow(copy, this.barcodeCounts(rows), this.firstByName(rows));
      return { errors: copy.errors, status: copy.status, detail: this.rowDetail(copy) };
    },
    // Lo que escribió el formulario, en una fila: los montos van como texto y se convierten como al leer el Excel; si no son un número, la fila guarda lo escrito y muestra "Número inválido".
    applyFormChanges(row, changes) {
      const amounts = AMOUNTS.filter((field) => field in changes);
      this.applyRowChanges(row, { ...changes, ...Object.fromEntries(amounts.map((field) => [field, this.parseAmount(changes[field])])) });
      amounts.filter((field) => row[field] === null).forEach((field) => { row.raw[field] = changes[field]; });
    },
    // Aplicar: lo cambiado se escribe como al editar la celda, se revalida y la tabla se consulta de nuevo; "y siguiente" abre la próxima fila con errores.
    applyRow(changes, next = false) {
      const row = this.editingRow;
      if (Object.keys(changes).length) {
        this.applyFormChanges(row, changes);
        this.validateAll();
        this.repaintGrid(true);
      }
      this.editingRow = (next && this.nextErrorRow(row)) || row;
      this.rowDialog = this.editingRow !== row;
    },
    nextErrorRow(row) {
      return this.rows.slice(this.rows.indexOf(row) + 1).find((item) => item.status === "Con errores");
    },

    // ---------- Segmentación nueva ----------
    // Segmentaciones nuevas de la fila que sí se pueden crear: un hijo nuevo necesita su padre (existente o también nuevo).
    creatableSegments(row) {
      const isNew = (field) => !!row.raw[field] && !row[field] && !row.ambiguous[field];
      const family = isNew("family");
      const category = isNew("category") && (!!row.family || family);
      const subcategory = isNew("subcategory") && (!!row.category || category);
      return { family, category, subcategory };
    },
    confirmCreateSegmentation() {
      this.segmentationThenImport = false;
      this.segmentationDialog = true;
    },
    async runCreateSegmentation() {
      this.segmentationDialog = false;
      const created = await this.createSegmentation();
      if (created && this.segmentationThenImport) this.continueToImport();
    },
    skipSegmentations() {
      this.segmentationDialog = false;
      this.continueToImport();
    },
    async createSegmentation() {
      this.loading = true;
      this.busyText = `Creando ${plural(this.newSegmentationCount, "segmentación", "segmentaciones")}…`;
      try {
        await this.$API.itemtypes.createData(this.segmentationToCreate);
        const [families, categories, subcategories] = await Promise.all([
          this.$API.itemtypes.getFamilies(),
          this.$API.itemtypes.getCategories(),
          this.$API.itemtypes.getSubs(),
        ]);
        this.families = families || [];
        this.categories = categories || [];
        this.subcategories = subcategories || [];
        this.rows.filter((row) => !row.saved).forEach((row) => this.resolveSegmentation(row));
        this.validateAll();
        this.repaintGrid();
        return true;
      } catch (error) {
        this.showAlert("danger", "Error", `No se pudieron crear las segmentaciones: ${this.errorMessage(error)}`);
        return false;
      } finally {
        this.loading = false;
      }
    },

    // ---------- Guardar ----------
    // Guardar: primero las segmentaciones pendientes (sin ellas esas filas no se importan), después la confirmación de importar.
    confirmImport() {
      if (this.saveDisabled) return;
      if (this.newSegmentationCount) {
        this.segmentationThenImport = true;
        this.segmentationDialog = true;
        return;
      }
      this.importDialog = true;
    },
    // Segmentaciones resueltas (creadas u omitidas): sigue la confirmación de importar, si ya se puede.
    continueToImport() {
      if (this.saveBlockReason) {
        this.nudgeWarehouse();
        return;
      }
      this.importDialog = true;
    },
    runImport() {
      this.importDialog = false;
      this.saveImport();
    },
    async saveImport() {
      if (this.saveBlockReason) return;
      const batch = this.selectedValidRows;
      const payload = {
        warehouse: this.needsWarehouse ? this.warehouse : null,
        products: batch.map((row) => ({
          name: row.name,
          comercialName: row.comercialName,
          description: row.description,
          measureUnit: row.measureUnit,
          price: row.price,
          cost: row.cost,
          stock: row.stock,
          barcode: row.barcode,
          aditionalType: row.aditionalType,
          family: row.family,
          category: row.category,
          subcategory: row.subcategory,
          idProvider: row.idProvider,
        })),
      };

      // BUG-103: mismo contenido que el intento anterior = reintento; viaja la misma clave y el backend devuelve lo ya
      // guardado en vez de crear otra vez. Si cambia algo (selección, celdas, almacén) la clave es nueva.
      const fingerprint = JSON.stringify(payload);
      if (!this.importAttempt || this.importAttempt.fingerprint !== fingerprint) this.importAttempt = { key: getUUIDv4(), fingerprint };
      payload.importKey = this.importAttempt.key;

      this.loading = true;
      this.busyText = `Guardando ${plural(batch.length, "producto", "productos")}…`;
      let imported = false;
      try {
        const response = await this.$API.products.importProductsV2(payload);
        imported = true;
        this.importAttempt = null; // guardado: el próximo lote, aunque sea idéntico, es otra importación
        // Los índices de la respuesta son posiciones en el lote enviado, no en la tabla.
        response.data.created.forEach(({ index, ID_Item }) => {
          batch[index].saved = true;
          batch[index].ID_Item = ID_Item;
        });
        this.result = { created: response.data.created.length };
        this.rememberImport(response.data.created.length);
        const savedKeys = new Set(batch.map((row) => row._rowId));
        this.selectedRowKeys = this.selectedRowKeys.filter((key) => !savedKeys.has(key));
      } catch (error) {
        const errors = (error && error.errors) || [];
        errors.forEach(({ path, message }) => {
          const [scope, index, field] = String(path).split(".");
          if (scope === "warehouse") this.warehouseError = message;
          else if (scope === "products" && batch[index]) {
            batch[index].serverErrors = { ...batch[index].serverErrors, [field || "name"]: message };
            batch[index].inReview = true; // en el paso de revisión la tabla solo muestra esas filas: la rechazada también debe verse
          }
        });
        this.showAlert(
          errors.length ? "warning" : "danger",
          errors.length ? "Revise las celdas en rojo" : "Error",
          errors.length ? this.serverErrorSummary(errors) : this.errorMessage(error)
        );
      } finally {
        this.validateAll();
        // Ya importado lo importado: si quedan filas con problemas, la tabla muestra solo ellas.
        if (imported && this.rowCounts.attention > 0) {
          this.reviewEnabled = true;
          this.captureReview();
          this.view = "attention";
        }
        this.repaintGrid();
        this.loading = false;
      }
    },
    // ---------- Salir con trabajo pendiente ----------
    askLeave() {
      this.leaveAction = "close";
      this.leaveDialog = true;
    },
    // "Cargar Excel": con un archivo ya cargado se pregunta ANTES de abrir el selector; solo si confirma se abre y se reemplaza.
    pickExcel() {
      if (this.loading) return;
      if (this.rows.length) {
        this.leaveAction = "replace";
        this.leaveDialog = true;
        return;
      }
      this.$refs.excelInput.click();
    },
    // Limpiar con filas sin importar pide confirmación (un clic sin querer perdería el trabajo); sin pendientes limpia directo.
    askClear() {
      if (!this.hasUnsavedWork) {
        this.clearImport();
        return;
      }
      this.leaveAction = "clear";
      this.leaveDialog = true;
    },
    runLeave() {
      this.leaveDialog = false;
      if (this.leaveAction === "duplicates") {
        this.removeRows(this.duplicateRows);
        return;
      }
      if (this.leaveAction === "clear") {
        this.clearImport();
        return;
      }
      if (this.leaveAction === "replace") {
        // Sigue dentro del clic de confirmar: el navegador deja abrir el selector de archivos.
        if (this.$refs.excelInput) this.$refs.excelInput.click();
        return;
      }
      this.$emit("close");
    },
    // ---------- Quitar filas (nombre repetido) ----------
    askRemoveDuplicates() {
      this.leaveAction = "duplicates";
      this.leaveDialog = true;
    },
    // Quita filas de la importación (no del Excel). Sin filas, vuelve a la pantalla inicial como Limpiar.
    removeRows(rowsToRemove) {
      const removed = new Set(rowsToRemove.map((row) => row._rowId));
      this.rowDialog = false;
      this.rows = this.rows.filter((row) => !removed.has(row._rowId));
      this.selectedRowKeys = this.selectedRowKeys.filter((key) => !removed.has(key));
      if (!this.rows.length) {
        this.clearImport();
        return;
      }
      this.validateAll();
      this.repaintGrid(true);
    },
    // ---------- Corrección en bloque ----------
    bulkSource(row, field) {
      return (field === "idProvider" ? row.raw.provider || row.raw.nit : row.raw[field]) || "(vacío)";
    },
    bulkItems(field) {
      if (field === "measureUnit") return this.units.map((unit) => ({ value: unit.Valores, text: unit.Valores }));
      if (field === "idProvider") return this.providerOptions.filter((option) => option.ID_Provider !== "").map((option) => ({ value: option.ID_Provider, text: option.label }));
      return this.aditionalTypes.filter((item) => item.value !== "");
    },
    applyBulk(group) {
      const value = this.bulkChoice[group.key];
      if (value === undefined || value === null || value === "") return;
      // Igual que editar la celda a mano (onRowUpdated): lo elegido reemplaza el texto del Excel y los errores del servidor.
      group.rows.forEach((row) => {
        row[group.field] = value;
        const serverErrors = { ...row.serverErrors };
        delete serverErrors[group.field];
        row.serverErrors = serverErrors;
        row.ambiguous = { ...row.ambiguous, [group.field]: false };
        const raw = { ...row.raw };
        if (group.field === "idProvider") { raw.provider = ""; raw.nit = ""; } else raw[group.field] = "";
        row.raw = raw;
      });
      this.$delete(this.bulkChoice, group.key);
      this.validateAll();
      this.repaintGrid();
    },
    // ---------- Archivo ya importado (solo en este navegador) ----------
    recentImports() {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const all = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
        return { scope: `${user.businessid}:${user.branch}`, all: Array.isArray(all) ? all : [] };
      } catch (error) {
        return { scope: "", all: [] };
      }
    },
    findPreviousImport(hash) {
      const { scope, all } = this.recentImports();
      return all.find((item) => item.hash === hash && item.scope === scope) || null;
    },
    rememberImport(count) {
      try {
        const { scope, all } = this.recentImports();
        const next = [{ hash: this.fileHash, scope, at: Date.now(), count }, ...all.filter((item) => !(item.hash === this.fileHash && item.scope === scope))].slice(0, 30);
        localStorage.setItem(RECENT_KEY, JSON.stringify(next));
      } catch (error) {
        // Sin almacenamiento local el aviso simplemente no aparece.
      }
    },
    // Congela la lista de revisión: las filas que hoy necesitan atención.
    captureReview() {
      this.rows.forEach((row) => {
        row.inReview = !row.saved && (row.status === "Con errores" || row.status === "Nueva");
      });
    },
    openReview() {
      this.captureReview();
      this.view = "attention";
    },
    // Cerrar la ventana de "Agregar nuevo producto" (la X de arriba); la pantalla que contiene a V2 hace el cierre.
    closeImport() {
      this.$emit("close");
    },
    // Cerrar la importación = lo mismo que Limpiar: se descarta lo que quedó sin corregir y se vuelve a la pantalla inicial.
    runIgnore() {
      this.ignoreDialog = false;
      this.clearImport();
    },
    // Por qué rechazó el servidor, en claro: cuántas filas por motivo (los 3 más frecuentes), no solo "hay filas con errores".
    serverErrorSummary(errors) {
      const rowsByReason = new Map();
      errors.forEach(({ path, message }) => {
        const [scope, index] = String(path).split(".");
        const key = scope === "products" ? `fila ${index}` : "general";
        rowsByReason.set(message, (rowsByReason.get(message) || new Set()).add(key));
      });
      const reasons = [...rowsByReason.entries()].sort((a, b) => b[1].size - a[1].size);
      const lines = reasons.slice(0, 3).map(([message, keys]) => (keys.has("general") && keys.size === 1 ? message : `${plural(keys.size, "fila", "filas")}: ${message}`));
      const repeated = reasons.some(([message]) => /ya existe/i.test(message))
        ? " Si ya importó este archivo, esos productos ya existen: cambie el nombre o quite esas filas."
        : "";
      return `${lines.join(". ")}.${repeated} Corrija las celdas marcadas y vuelva a guardar.`;
    },
    clearImport() {
      this.previousImport = null;
      this.fileHash = "";
      this.bulkChoice = {};
      this.reviewEnabled = false;
      this.view = "all";
      this.rows = [];
      this.selectedRowKeys = [];
      this.result = null;
      this.warehouseError = "";
    },

    // ---------- Alertas ----------
    errorMessage(error) {
      if (!error) return "Error desconocido";
      if (typeof error === "string") return error;
      return error.message || "Error desconocido";
    },
    showAlert(type, header, body, options = null) {
      this.alert = { type, show: true, header, body, options };
    },
    closeAlert() {
      this.alert.show = false;
    },
    acceptAlert() {
      this.alert.show = false;
    },
  },
};
</script>

<style scoped>
.product-import-v2 {
  --imp-accent: #3498db;
  --imp-ink: #1b4f72;
  --imp-tint: #f4f9fd;
  --imp-tint-strong: #e3f0fa;
}

.busy-line {
  min-height: 28px;
}

.is-locked {
  pointer-events: none;
  opacity: 0.6;
}

/* Resumen: tres cards con ícono animado y un mensaje de estado. Cada tono fija fondo del círculo y color del texto. */
.summary {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 28px;
}

.tone-info {
  --tone-bg: #e3f0fa;
  --tone-ink: #1b4f72;
}

.tone-success {
  --tone-bg: #e6f6ec;
  --tone-ink: #14532d;
}

.tone-danger {
  --tone-bg: #fdecea;
  --tone-ink: #922b21;
}

.tone-warning {
  --tone-bg: #fff4e5;
  --tone-ink: #7e5109;
}

.tone-muted {
  --tone-bg: #eef1f4;
  --tone-ink: rgba(0, 0, 0, 0.6);
}

.stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(230px, 1fr));
  gap: 16px;
}

.stat {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background-color: var(--imp-tint);
}

/* Contenido de los slots de los diálogos de confirmación. */
.confirm-sub {
  margin-top: 2px;
  font-size: 0.75rem;
  color: rgba(0, 0, 0, 0.65);
}

.confirm-tag {
  display: inline-block;
  margin: 0 6px 4px 0;
  padding: 2px 10px;
  border-radius: 999px;
  background-color: #fff4e5;
  color: #7e5109;
  font-size: 0.8125rem;
}

.stat__icon,
.banner__icon {
  display: grid;
  flex: 0 0 auto;
  place-items: center;
  border-radius: 50%;
  background-color: var(--tone-bg);
}

.stat__icon {
  width: 52px;
  height: 52px;
}

.stat__art {
  width: 32px;
  height: 32px;
}

.tone-muted .stat__art {
  filter: grayscale(1);
}

/* V1 agranda este ícono en toda la app (".mdi-file-excel::before { font-size: 32px !important }" sin scope): aquí vuelve al tamaño de los demás. */
.product-import-v2 ::v-deep .v-btn .mdi-file-excel::before {
  font-size: 18px !important;
}

/* Regla global "table i { color: #3498db !important }": sin esto los íconos de los chips de la tabla salen azules. */
.product-import-v2 ::v-deep .dx-datagrid .v-chip .v-icon {
  color: #fff !important;
}

.stat__text {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 8px;
}

.stat__value {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.1;
  color: var(--tone-ink);
  font-variant-numeric: tabular-nums;
}

.stat__label {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.65);
}

.banner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px;
  background-color: var(--tone-bg);
}

.banner__icon {
  width: 44px;
  height: 44px;
  background-color: rgba(255, 255, 255, 0.7);
}

.banner__art {
  width: 28px;
  height: 28px;
}

.banner__text {
  flex: 1 1 160px;
}

.banner__action {
  margin-left: auto;
}

/* Corrección en bloque dentro del paso de revisión. */
.review__bulk {
  flex: 1 1 100%;
  padding: 12px 14px;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.7);
}

.review__bulk-title {
  margin-bottom: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--tone-ink);
}

.review__bulk-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 12px;
  padding: 4px 0;
}

.review__bulk-label {
  flex: 1 1 220px;
  font-size: 0.875rem;
}

.review__bulk-select {
  flex: 0 1 260px;
  min-width: 200px;
}

/* Paso de revisión: las acciones ocupan una fila propia bajo el texto. */
.review__actions {
  display: flex;
  flex: 1 1 100%;
  flex-wrap: wrap;
  gap: 8px;
}

.banner__title {
  font-size: 1rem;
  font-weight: 700;
  color: var(--tone-ink);
}

.banner__detail {
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.72);
}

/* Pantalla vacía: carga + guía */
.panel {
  padding: 24px;
  border-radius: 12px;
  background-color: var(--imp-tint);
}

/* La zona de carga ocupa el alto sobrante: alinea el borde inferior con la columna de ayuda. */
.upload-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.upload-panel .v-btn {
  align-self: flex-start;
}

.panel-muted {
  color: rgba(0, 0, 0, 0.6);
}

.dropzone {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 232px;
  padding: 16px;
  border: 2px dashed var(--imp-accent);
  border-radius: 12px;
  background-color: #fff;
  text-align: center;
  cursor: pointer;
  transition: background-color 0.2s ease-out, border-color 0.2s ease-out;
}

/* Solo la zona recibe los eventos de arrastre: sin esto, dragleave parpadea al pasar sobre los hijos. */
.dropzone > * {
  pointer-events: none;
}

.dropzone:hover,
.dropzone--over {
  background-color: var(--imp-tint-strong);
  border-color: var(--imp-ink);
}

.dropzone:focus-visible {
  outline: 3px solid var(--imp-ink);
  outline-offset: 3px;
}

.dropzone--busy {
  cursor: progress;
}

.dropzone__art {
  width: 160px;
  height: 160px;
}

.dropzone__title {
  margin-top: 4px;
  font-size: 1rem;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
}

.dropzone__link {
  font-weight: 600;
  color: var(--imp-ink);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.steps {
  margin: 0;
  padding: 0;
  list-style: none;
}

.steps li {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.steps li + li {
  margin-top: 14px;
}

.steps__number {
  flex: 0 0 28px;
  height: 28px;
  border-radius: 8px;
  background-color: var(--imp-tint-strong);
  color: var(--imp-ink);
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 28px;
  text-align: center;
}

/* Sacudida vertical corta (3 vaivenes, sin rebote): pide elegir el almacén. */
.shake-y {
  animation: shake-y 0.4s ease-in-out;
}

@keyframes shake-y {
  0%,
  100% {
    transform: translateY(0);
  }
  15% {
    transform: translateY(-6px);
  }
  30% {
    transform: translateY(6px);
  }
  45% {
    transform: translateY(-5px);
  }
  60% {
    transform: translateY(5px);
  }
  75% {
    transform: translateY(-2px);
  }
  90% {
    transform: translateY(2px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .dropzone {
    transition: none;
  }

  .shake-y {
    animation: none;
  }
}

.product-import-v2 ::v-deep .cell-error {
  background-color: #fdecea !important;
  box-shadow: inset 0 0 0 1px #e53935;
}

/* Texto largo a la izquierda: estilos sin scoped de veterinaria centran todas las celdas con !important. */
.product-import-v2 ::v-deep .dx-datagrid-content td.detail-cell {
  text-align: left !important;
  white-space: normal;
  overflow-wrap: anywhere;
  line-height: 1.35;
}

.product-import-v2 ::v-deep .cell-new {
  background-color: #fff4e5 !important;
  box-shadow: inset 0 0 0 1px #fb8c00;
}
</style>
